"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import type { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { useWaveDropsLeaderboard } from "@/hooks/useWaveDropsLeaderboard";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React from "react";
import { WaveLeaderboardDrop } from "./WaveLeaderboardDrop";
import { WaveLeaderboardEmptyState } from "./WaveLeaderboardEmptyState";
import { WaveLeaderboardLoading } from "./WaveLeaderboardLoading";
import { WaveLeaderboardLoadingBar } from "./WaveLeaderboardLoadingBar";
interface WaveLeaderboardDropsProps {
  readonly wave: ApiWave;
  readonly sort: WaveDropsLeaderboardSort;
  readonly onCreateDrop?: (() => void) | undefined;
  readonly curatedByGroupId?: string | undefined;
  readonly minPrice?: number | undefined;
  readonly maxPrice?: number | undefined;
  readonly priceCurrency?: string | undefined;
}
export const WaveLeaderboardDrops: React.FC<WaveLeaderboardDropsProps> = t0 => {
  const $ = _c(39);
  const {
    wave,
    sort,
    onCreateDrop,
    curatedByGroupId,
    minPrice,
    maxPrice,
    priceCurrency
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== curatedByGroupId || $[1] !== maxPrice || $[2] !== minPrice || $[3] !== priceCurrency || $[4] !== sort || $[5] !== wave.id) {
    t1 = {
      waveId: wave.id,
      sort,
      curatedByGroupId,
      minPrice,
      maxPrice,
      priceCurrency
    };
    $[0] = curatedByGroupId;
    $[1] = maxPrice;
    $[2] = minPrice;
    $[3] = priceCurrency;
    $[4] = sort;
    $[5] = wave.id;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useWaveDropsLeaderboard(t1);
  let t2;
  if ($[7] !== fetchNextPage || $[8] !== hasNextPage || $[9] !== isFetching || $[10] !== isFetchingNextPage) {
    t2 = async () => {
      if (hasNextPage && !isFetching && !isFetchingNextPage) {
        await fetchNextPage();
      }
    };
    $[7] = fetchNextPage;
    $[8] = hasNextPage;
    $[9] = isFetching;
    $[10] = isFetchingNextPage;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const intersectionElementRef = useIntersectionObserver(t2);
  let t3;
  if ($[12] !== pathname || $[13] !== router || $[14] !== searchParams) {
    t3 = drop => {
      const params = new URLSearchParams(searchParams.toString());
      params.set("drop", drop.id);
      router.push(`${pathname}?${params.toString()}`);
    };
    $[12] = pathname;
    $[13] = router;
    $[14] = searchParams;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const onDropClick = t3;
  let t4;
  if ($[16] !== refetch) {
    t4 = () => {
      refetch();
    };
    $[16] = refetch;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const handleSourceDropDeleted = t4;
  if (isFetching && drops.length === 0) {
    let t5;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <WaveLeaderboardLoading />;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    return t5;
  }
  if (drops.length === 0) {
    let t5;
    if ($[19] !== onCreateDrop || $[20] !== wave) {
      t5 = <WaveLeaderboardEmptyState onCreateDrop={onCreateDrop} wave={wave} />;
      $[19] = onCreateDrop;
      $[20] = wave;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    return t5;
  }
  let t5;
  if ($[22] !== drops || $[23] !== handleSourceDropDeleted || $[24] !== onDropClick || $[25] !== wave) {
    let t6;
    if ($[27] !== handleSourceDropDeleted || $[28] !== onDropClick || $[29] !== wave) {
      t6 = drop_0 => <WaveLeaderboardDrop key={drop_0.id} drop={drop_0} wave={wave} onDropClick={onDropClick} onSourceDropDeleted={handleSourceDropDeleted} />;
      $[27] = handleSourceDropDeleted;
      $[28] = onDropClick;
      $[29] = wave;
      $[30] = t6;
    } else {
      t6 = $[30];
    }
    t5 = drops.map(t6);
    $[22] = drops;
    $[23] = handleSourceDropDeleted;
    $[24] = onDropClick;
    $[25] = wave;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[31] !== isFetchingNextPage) {
    t6 = isFetchingNextPage && <WaveLeaderboardLoadingBar />;
    $[31] = isFetchingNextPage;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] !== intersectionElementRef) {
    t7 = <div ref={intersectionElementRef} />;
    $[33] = intersectionElementRef;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== t5 || $[36] !== t6 || $[37] !== t7) {
    t8 = <div className="tw-space-y-4">{t5}{t6}{t7}</div>;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  return t8;
};